import { createIntl, createIntlCache, type PrimitiveType } from 'react-intl';
import csMessages from '@/lang/cs.json';
import enMessages from '@/lang/en.json';
import plMessages from '@/lang/pl.json';
import skMessages from '@/lang/sk.json';

export const INVENTORY_LOCALES = ['en', 'cs', 'sk', 'pl'] as const;
export type InventoryLocale = (typeof INVENTORY_LOCALES)[number];

export const DEFAULT_INVENTORY_LOCALE: InventoryLocale = 'en';
const STORAGE_KEY = 'enmon-inventory-locale';

const messagesByLocale: Record<InventoryLocale, Record<string, string>> = {
  cs: csMessages,
  en: enMessages,
  pl: plMessages,
  sk: skMessages,
};
const intlCache = createIntlCache();

export function setInventoryLocale(locale: InventoryLocale) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, locale);
  }
}

export function getInventoryLocale(): InventoryLocale {
  return readStoredLocale() ?? readNavigatorLocale() ?? DEFAULT_INVENTORY_LOCALE;
}

export function getInventoryMessages(locale: InventoryLocale) {
  return messagesByLocale[locale];
}

export function translateInventoryText(key: string, _fallback: string, values: Record<string, PrimitiveType> = {}) {
  return createIntl(
    {
      defaultLocale: DEFAULT_INVENTORY_LOCALE,
      locale: getInventoryLocale(),
      messages: getInventoryMessages(getInventoryLocale()),
    },
    intlCache,
  ).formatMessage({ defaultMessage: _fallback, id: key }, values);
}

export function translateFirstInventoryText(
  keys: string[],
  fallback: string,
  values: Record<string, PrimitiveType> = {},
) {
  const locale = getInventoryLocale();
  const key = keys.find(
    candidate => messagesByLocale[locale][candidate] ?? messagesByLocale[DEFAULT_INVENTORY_LOCALE][candidate],
  );

  return key ? translateInventoryText(key, fallback, values) : fallback;
}

function readStoredLocale(): InventoryLocale | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return readInventoryLocale(window.localStorage.getItem(STORAGE_KEY));
}

function readNavigatorLocale(): InventoryLocale | null {
  if (typeof navigator === 'undefined') {
    return null;
  }

  return readInventoryLocale(navigator.language);
}

function readInventoryLocale(value: string | null | undefined): InventoryLocale | null {
  const normalizedLocale = value?.split('-')[0]?.toLowerCase();

  return INVENTORY_LOCALES.find(locale => locale === normalizedLocale) ?? null;
}
